import { X, UtensilsCrossed, ShoppingBag, Bike, MapPin, Calendar as CalendarIcon, Clock, Users, IndianRupee } from 'lucide-react';
import { getBusinessTitle, getBusinessLocation, formatTo24HourTime } from '../../utils/appBookingHelpers';

const BookingDetailsModal = ({ booking, onClose }) => {
    if (!booking) return null;

    const formattedTime = formatTo24HourTime(booking.bookedTime);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs animate-in fade-in duration-150"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative border border-slate-100 p-6 md:p-7 animate-in fade-in zoom-in-95 duration-200">
                {/* Top Right Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 w-8 h-8 rounded-full bg-slate-100/80 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-all cursor-pointer"
                    title="Close modal"
                >
                    <X size={16} />
                </button>

                {/* Top Left Icon + Activity Title Block */}
                <div className="flex items-start gap-4 mb-6 pr-8">
                    <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-[#067978] flex items-center justify-center flex-shrink-0">
                        {booking.type === 'Restaurant' ? (
                            <UtensilsCrossed className="w-7 h-7 text-[#067978]" />
                        ) : booking.type === 'Shop' ? (
                            <ShoppingBag className="w-7 h-7 text-[#067978]" />
                        ) : (
                            <Bike className="w-7 h-7 text-[#067978]" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <span className="text-xs font-semibold text-emerald-600 block">
                            {booking.type === 'Task' ? 'Activity' : booking.type}
                        </span>
                        <h2 className="text-xl font-bold text-slate-900 leading-snug mt-0.5 truncate">
                            {getBusinessTitle(booking)}
                        </h2>
                        <p className="flex items-center gap-1 text-[11px] text-slate-400 font-normal mt-1">
                            <MapPin size={10} className="text-emerald-600 shrink-0" />
                            <span className="truncate">{getBusinessLocation(booking)}</span>
                        </p>
                    </div>
                </div>

                {/* Guest & Status Row */}
                <div className="grid grid-cols-2 gap-4 py-5 border-t border-b border-slate-100 mb-6">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-full bg-emerald-50 text-[#067978] font-bold text-base flex items-center justify-center shrink-0">
                            {(booking.guest_name || booking.guestName || booking.booking_id?.name || 'M').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold text-slate-400">Guest</p>
                            <p className="text-base font-bold text-slate-900 mt-0.5 truncate">
                                {booking.guest_name || booking.guestName || booking.booking_id?.name || 'App User'}
                            </p>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs font-semibold text-slate-400 mb-1">Status</p>
                        <span className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                            {booking.status || 'Completed'}
                        </span>
                    </div>
                </div>

                {/* Details Grid with Vertical Separator */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-6 mb-6 text-xs">
                    {/* Left Column */}
                    <div className="space-y-5 border-r border-slate-100 pr-4">
                        <div>
                            <p className="text-xs font-semibold text-slate-400 mb-1 flex items-center gap-1.5">
                                <CalendarIcon size={14} className="text-slate-400 shrink-0" /> Date
                            </p>
                            <p className="text-xs font-semibold text-slate-800">
                                {new Date(booking.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-semibold text-slate-400 mb-1 flex items-center gap-1.5">
                                <Clock size={14} className="text-slate-400 shrink-0" /> Created at
                            </p>
                            <p className="text-xs font-medium text-slate-700">
                                {booking.createdAt ? new Date(booking.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : 'N/A'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-semibold text-slate-400 mb-1 flex items-center gap-1.5">
                                <IndianRupee size={14} className="text-slate-400 shrink-0" /> Bill amount
                            </p>
                            <p className="text-base font-bold text-slate-800 font-mono">
                                ₹{(booking.final_bill_amount || 0).toLocaleString('en-IN')}
                            </p>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-5 pl-2">
                        <div>
                            <p className="text-xs font-semibold text-slate-400 mb-1 flex items-center gap-1.5">
                                <Clock size={14} className="text-slate-400 shrink-0" /> Time
                            </p>
                            <p className="text-xs font-semibold text-slate-800 font-mono">
                                {formattedTime || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-semibold text-slate-400 mb-1 flex items-center gap-1.5">
                                <Users size={14} className="text-slate-400 shrink-0" /> Total members
                            </p>
                            <p className="text-xs font-medium text-slate-700">
                                {booking.totalMembers || 1}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-semibold text-slate-400 mb-1 flex items-center gap-1.5">
                                <IndianRupee size={14} className="text-slate-400 shrink-0" /> Your commission
                            </p>
                            <p className="text-base font-bold text-emerald-600 font-mono">
                                ₹{(booking.computed_agent_cut || 0).toLocaleString('en-IN')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Bottom Action */}
                <div className="flex justify-end pt-2">
                    <button
                        onClick={onClose}
                        className="px-7 py-2.5 bg-[#067978] hover:bg-[#056463] text-white text-xs font-bold rounded-xl transition-all shadow-xs active:scale-95 cursor-pointer"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BookingDetailsModal;
